// apps/patient/src/contexts/PatientContext.tsx

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { patientApiService as apiClient } from '../services/api';

// Note - 'patient' is equivalent to 'user' in other contexts. This can be confusing, because there is a separate patient object, which will be accessible as patient.patient

// Declare FaceTecSDK on window (JavaScript SDK)
declare global {
  interface Window {
    FaceTecSDK: any;
  }
}

interface LoginIdentifier {
  phone?: string;
  email?: string;
}

interface PatientContextType {
  patient: any;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (identifier: LoginIdentifier) => Promise<{ success: boolean; requiresVerification?: boolean }>;
  verifyPin: (identifier: LoginIdentifier, code: string) => Promise<{ success: boolean; userId?: string; sessionToken?: string; biometricMfa?: boolean; authToken?: string; isNewUser?: boolean; needsIdentity?: boolean }>;
  resolveIdentity: (data: { firstName: string; lastName: string; birthday: string }) => Promise<{ success: boolean; authToken?: string; isNewUser?: boolean; needsProvider?: boolean; message?: string }>;
  logout: () => void;
  refreshPatient: () => Promise<void>;
}

const PatientContext = createContext<PatientContextType | undefined>(undefined);

export const PatientProvider = ({ children }: { children: React.ReactNode }) => {
  const [patient, setPatient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Temporary storage for auth flow
  const [authFlowData, setAuthFlowData] = useState<{
    phone?: string;
    email?: string;
    verificationId?: string;
    userId?: string;
    sessionToken?: string;
  }>({});

  // Initialize patient from stored token
  useEffect(() => {
    const initializePatient = async () => {
      try {
        const token = apiClient.getToken();
        if (!token) {
          setLoading(false);
          return;
        }

        // Get patient profile using existing token
        const profile = await apiClient.getMe();
        setPatient(profile);
        setIsAuthenticated(true);
      } catch (err) {
        console.error('[PatientContext] Initialization error:', err);
        apiClient.clearToken();
      } finally {
        setLoading(false);
      }
    };

    initializePatient();
  }, []);

  // Step 1: Send verification code
  const login = async (identifier: LoginIdentifier): Promise<{ success: boolean; requiresVerification?: boolean }> => {
    try {
      setError(null);

      const response = await apiClient.sendVerificationCode({ phone: identifier.phone, email: identifier.email });

      if (response.success) {
        setAuthFlowData({
          phone: identifier.phone,
          email: identifier.email,
          verificationId: response.verification_id
        });
        return { success: true, requiresVerification: true };
      }

      throw new Error(response.message || 'Failed to send verification code');
    } catch (err: any) {
      setError(err.message);
      return { success: false };
    }
  };

  // Step 2: Verify PIN
  const verifyPin = async (identifier: LoginIdentifier, code: string): Promise<{ success: boolean; userId?: string; sessionToken?: string; authToken?: string; biometricMfa?: boolean; isNewUser?: boolean; needsIdentity?: boolean }> => {
    try {
      setError(null);

      if (!authFlowData.verificationId) {
        throw new Error('Verification ID not found. Please restart the login process.');
      }

      const response = await apiClient.verifyPinCode({
        phone: identifier.phone,
        email: identifier.email,
        code,
        verificationId: authFlowData.verificationId
      });

      if (response.success) {
        // Check if identity resolution is needed
        if (response.needs_identity) {
          setAuthFlowData({
            ...authFlowData,
            sessionToken: response.session_token
          });
          return { success: true, needsIdentity: true };
        }

        // If auth_token is returned (biometric disabled), complete authentication
        if (response.auth_token) {
          apiClient.setToken(response.auth_token);

          const profile = await apiClient.getMe();

          setPatient(profile);
          setIsAuthenticated(true);
        }

        setAuthFlowData({
          ...authFlowData,
          userId: response.user_id,
          sessionToken: response.session_token
        });

        return {
          success: true,
          userId: response.user_id,
          sessionToken: response.session_token,
          authToken: response.auth_token,
          biometricMfa: response.biometric_mfa,
          isNewUser: response.isNewUser
        };
      }

      throw new Error(response.message || 'Invalid verification code');
    } catch (err: any) {
      setError(err.message);
      return { success: false };
    }
  };

  // Step 2b: Resolve identity (when verify-pin returns needs_identity)
  const resolveIdentity = async (data: { firstName: string; lastName: string; birthday: string }): Promise<{ success: boolean; authToken?: string; isNewUser?: boolean; needsProvider?: boolean; message?: string }> => {
    try {
      setError(null);

      const response = await apiClient.resolveIdentity({
        verificationId: authFlowData.verificationId!,
        sessionToken: authFlowData.sessionToken!,
        firstName: data.firstName,
        lastName: data.lastName,
        birthday: data.birthday,
        phone: authFlowData.phone,
        email: authFlowData.email
      });

      if (response.success) {
        apiClient.setToken(response.auth_token);

        const profile = await apiClient.getMe();

        setPatient(profile);
        setIsAuthenticated(true);

        return {
          success: true,
          authToken: response.auth_token,
          isNewUser: response.isNewUser
        };
      }

      // Clinical platform — no matching patient
      if (response.needs_provider) {
        return {
          success: false,
          needsProvider: true,
          message: response.message
        };
      }

      throw new Error(response.message || 'Identity resolution failed');
    } catch (err: any) {
      setError(err.message);
      return { success: false };
    }
  };

  const logout = () => {
    apiClient.clearToken();
    setPatient(null);
    setIsAuthenticated(false);
    setAuthFlowData({});
  };

  const refreshPatient = async () => {
    if (!isAuthenticated) return;

    try {
      const profile = await apiClient.getMe();
      setPatient(profile);
    } catch (err) {
      console.error('[PatientContext] Failed to refresh patient:', err);
    }
  };

  // Token validation interval
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkToken = async () => {
      try {
        await apiClient.getMe();
      } catch (err) {
        console.error('[PatientContext] Token validation failed:', err);
        logout();
      }
    };

    const interval = setInterval(checkToken, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const value: PatientContextType = {
    patient,
    loading,
    error,
    isAuthenticated,
    login,
    verifyPin,
    resolveIdentity,
    logout,
    refreshPatient
  };

  return (
    <PatientContext.Provider value={value}>
      {children}
    </PatientContext.Provider>
  );
};

export const usePatient = () => {
  const context = useContext(PatientContext);
  if (!context) {
    throw new Error('usePatient must be used within PatientProvider');
  }
  return context;
};